import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const SOLUTIONS = [
  {
    audience: "For Large & Leading Pharma",
    title: "Mature & Tail-End Brand Portfolio Optimization",
    challenge: "50% of portfolio is mature or tail-end but gets <10% attention.",
    solution:
      "High-ROI Delta Field Force as your dedicated 'Team B' — focused entirely on consolidation and penetration of your mature and tail-end brands.",
    benefits: [
      "40–50% OPEX reduction vs traditional field force",
      "Dedicated attention to brands needing lifecycle extension",
      "Performance-based revenue sharing — risk reduced",
      "Break-even in 1–2 years vs 3–4 years traditional",
    ],
    cta: "Optimize Your Mature & Tail-End Portfolio",
  },
  {
    audience: "For SMEs & Mid-Size Healthcare",
    title: "Turnkey Market Growth Engine",
    challenge: "Limited budgets, can't afford expensive market presence to grow.",
    solution:
      "Turnkey sales & marketing without building high-cost in-house teams. Access our network on day one.",
    benefits: [
      "Professional field force with no infrastructure investment",
      "60,000+ chemists & 55,000+ HCPs — no recruitment burden",
      "Scalable coverage — pay for results, expand as you grow",
      "Extra-urban market penetration from day one",
    ],
    cta: "Activate Growth Engine",
  },
  {
    audience: "For Multinational Companies",
    title: "India Market Entry Partnership",
    challenge: "India's extra-urban market is vast, complex, and unfamiliar.",
    solution:
      "Partner with Medstry to enter India with confidence. We provide established networks, regulatory know-how, and proven execution.",
    benefits: [
      "Instant access to 300+ market HQs across India",
      "Established HCP and chemist relationships in extra-urban India",
      "Local market intelligence and cultural expertise",
      "Compliant, scalable partnership model",
    ],
    cta: "Plan India Entry",
  },
];

export default function Solutions() {
  return (
    <section className="py-20 md:py-32" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">
            Solutions by Segment
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.02] text-slate-900">
            The right solution <br />
            <span className="font-editorial italic font-medium text-slate-700">for your situation.</span>
          </h2>
        </div>

        <div className="space-y-px bg-slate-200">
          {SOLUTIONS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`solution-${i}`}
              className="bg-white p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
            >
              <div className="lg:col-span-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700 mb-3">
                  {s.audience}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4">{s.title}</h3>
                <div className="text-sm text-slate-500 mb-2 font-semibold uppercase tracking-wide">Challenge</div>
                <p className="text-slate-700 leading-relaxed">{s.challenge}</p>
              </div>

              <div className="lg:col-span-5">
                <div className="text-sm text-slate-500 mb-2 font-semibold uppercase tracking-wide">
                  Our Solution
                </div>
                <p className="text-slate-800 leading-relaxed mb-6">{s.solution}</p>
                <ul className="space-y-3">
                  {s.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="h-5 w-5 shrink-0 inline-flex items-center justify-center bg-teal-50 mt-0.5">
                        <Check className="h-3.5 w-3.5 text-teal-700" strokeWidth={2.5} />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-3 flex lg:items-end">
                <a
                  href="/#contact"
                  data-testid={`solution-cta-${i}`}
                  className="inline-flex items-center justify-between gap-2 btn-primary px-5 py-3.5 text-sm font-semibold w-full"
                >
                  {s.cta} <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
